// src/components/CameraModal.js
import React, { useEffect, useRef, useState } from "react";
import pb from "../lib/pb";
import { useAuth } from "../contexts/AuthContext";

/* Helpers (unchanged) */
function genKey() {
  const arr = window.crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...arr));
}
async function encryptText(base64Key, text) {
  const keyBytes = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-GCM",
    true,
    ["encrypt"]
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(text);
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, enc);
  const combined = new Uint8Array(iv.byteLength + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.byteLength);
  return btoa(String.fromCharCode(...combined));
}

export default function CameraModal({ open, onClose }) {
  const { user } = useAuth();

  /* ---------------- existing capture state ---------------- */
  const [mode, setMode] = useState("image");
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [caption, setCaption] = useState("");
  const [license, setLicense] = useState("Public");
  const [timer, setTimer] = useState(0);
  const [durationLimit] = useState(300);

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunkBufferRef = useRef([]);
  const progressIntervalRef = useRef(null);

  /* ---------------- editor state (worker) ---------------- */
  const [isEditing, setIsEditing] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [workerAvailable, setWorkerAvailable] = useState(false);
  const workerRef = useRef(null);
  const displayCanvasRef = useRef(null);       // canvas shown to user while editing (processed frames drawn here)
  const hiddenVideoRef = useRef(null);         // internal video for frame grabbing when editing videos
  const processing = useRef(false);
  const [workerSettings, setWorkerSettings] = useState({
    filter: 'none',
    adjustments: { brightness:0, contrast:0, saturation:0, hue:0, sharpen:0, blur:0 },
    rotate: 0,
    flipH: true
  });
  const frameLoopRef = useRef(null);

  /* ---------------- startStream / stopStream (unchanged) ---------------- */
  const startStream = async () => {
    stopStream();
    try {
      let constraints = {};
      if (mode === "image" || mode === "video" || mode === "live") {
        constraints = { video: { facingMode: "user" }, audio: mode === "video" || mode === "live" };
      } else if (mode === "audio") {
        constraints = { audio: true, video: false };
      }
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(s);
      if (videoRef.current && mode !== 'audio') {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        videoRef.current.style.transform = "scaleX(-1)"; // selfie preview
        await videoRef.current.play().catch(()=>{});
      }
      if (audioRef.current && mode === 'audio') {
        audioRef.current.srcObject = s;
        audioRef.current.muted = true;
        await audioRef.current.play().catch(()=>{});
      }
    } catch (err) {
      console.error("getUserMedia error:", err);
      alert("Unable to access camera/microphone. Check permissions.");
    }
  };

  const stopStream = () => {
    try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop(); } catch {}
    if (stream) { stream.getTracks().forEach(t => t.stop()); }
    setStream(null);
    clearInterval(progressIntervalRef.current);
    setRecording(false);
    setTimer(0);
  };

  /* ---------------- captureImage (unchanged behavior, then go to editor) ---------------- */
  const captureImage = async () => {
    if (!stream || !videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth || 1280;
    canvas.height = v.videoHeight || 720;
    const ctx = canvas.getContext('2d');

    // mirrored selfie draw
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
    setMediaBlob(blob);
    if (previewURL) URL.revokeObjectURL(previewURL);
    setPreviewURL(URL.createObjectURL(blob));

    // immediate editor
    setIsEditing(true);
    setEditorReady(false);
    stopStream();
  };

  /* ---------------- start/stop recording (unchanged logic mostly) ---------------- */
  const startRecording = async () => {
    if (!stream) { await startStream(); await new Promise(r => setTimeout(r,150)); }
    try {
      let options = {};
      if (mode === 'video') {
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) options.mimeType = 'video/webm;codecs=vp9';
        else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) options.mimeType = 'video/webm;codecs=vp8';
      } else if (mode === 'audio') {
        if (MediaRecorder.isTypeSupported('audio/webm')) options.mimeType = 'audio/webm';
      }
      const mr = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mr;
      chunkBufferRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunkBufferRef.current.push(e.data); };
      mr.onstop = () => {
        const chunks = chunkBufferRef.current.slice();
        if (!chunks.length) { setRecording(false); return; }
        const mime = chunks[0].type || (mode==='audio' ? 'audio/webm' : 'video/webm');
        const blob = new Blob(chunks, { type: mime });
        setMediaBlob(blob);
        if (previewURL) URL.revokeObjectURL(previewURL);
        setPreviewURL(URL.createObjectURL(blob));
        chunkBufferRef.current = [];
        setRecording(false);
        stopStream();
        if (mode === 'video' || mode === 'image') {
          setIsEditing(true);
          setEditorReady(false);
        }
      };
      mr.start(500);
      setRecording(true);
      progressIntervalRef.current = setInterval(() => {
        setTimer(t => {
          if (mode === 'video' && t+1 >= durationLimit) {
            try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop(); } catch {}
            return durationLimit;
          }
          return t+1;
        });
      }, 1000);
    } catch (err) {
      console.error('MediaRecorder start error', err);
      alert('Recording failed to start.');
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop(); } catch {}
    clearInterval(progressIntervalRef.current);
    setRecording(false);
  };

  const reRecord = () => {
    if (previewURL) URL.revokeObjectURL(previewURL);
    setPreviewURL(null);
    setMediaBlob(null);
    chunkBufferRef.current = [];
    setTimer(0);
    setIsEditing(false);
    startStream();
  };

  /* ---------------- worker lifecycle ---------------- */
  // create worker (from public/editor.worker.js) when needed
  const createWorker = () => {
    if (workerRef.current) return workerRef.current;
    try {
      const w = new Worker('/editor.worker.js');
      workerRef.current = w;
      setWorkerAvailable(true);

      w.onmessage = (ev) => {
        const { data } = ev;
        if (!data) return;
        if (data.type === 'processed' && data.bitmap) {
          // main thread receives processed ImageBitmap
          // draw to display canvas context
          const canvas = displayCanvasRef.current;
          if (!canvas) {
            // close bitmap if no canvas
            try { data.bitmap.close && data.bitmap.close(); } catch {}
            return;
          }
          const ctx = canvas.getContext('2d');
          // clear and draw
          ctx.setTransform(1,0,0,1,0,0);
          ctx.clearRect(0,0,canvas.width,canvas.height);
          ctx.drawImage(data.bitmap, 0, 0, canvas.width, canvas.height);
          // release transferable
          try { data.bitmap.close && data.bitmap.close(); } catch {}
        } else if (data.type === 'error') {
          console.warn('Worker error:', data.message);
        }
      };

      w.onerror = (err) => {
        console.error('Worker error', err);
        setWorkerAvailable(false);
      };

      // set initial settings
      w.postMessage({ type: 'init', settings: workerSettings });

      return w;
    } catch (err) {
      console.warn('Could not create worker:', err);
      setWorkerAvailable(false);
      return null;
    }
  };

  const terminateWorker = () => {
    try {
      workerRef.current && workerRef.current.postMessage({ type:'stop' });
      workerRef.current && workerRef.current.terminate();
    } catch {}
    workerRef.current = null;
    setWorkerAvailable(false);
  };

  /* ---------------- editor loop: capture frames, send to worker ---------------- */
  // For video editing we create a hidden video element and repeatedly capture frames via createImageBitmap
  const startFrameLoopToWorker = async () => {
    if (!workerRef.current) createWorker();
    if (!workerRef.current) {
      // fallback to main-thread processing (not implemented here in full)
      console.warn('Worker unavailable � falling back to main-thread processing (limited).');
      return;
    }

    const canvas = displayCanvasRef.current;
    const ctx = canvas.getContext('2d');

    // If media is image: simply draw once, we still pass image bitmap to worker to apply filter so UI is consistent
    if (mediaBlob && mediaBlob.type.startsWith('image')) {
      const imgBitmap = await createImageBitmap(mediaBlob);
      // set canvas size to image size
      canvas.width = imgBitmap.width;
      canvas.height = imgBitmap.height;
      // send bitmap to worker as frame
      try {
        workerRef.current.postMessage({ type: 'frame', bitmap: imgBitmap }, [imgBitmap]);
      } catch (err) {
        // fallback: draw directly and let worker do nothing
        ctx.drawImage(imgBitmap, 0, 0, canvas.width, canvas.height);
        try { imgBitmap.close && imgBitmap.close(); } catch {}
      }
      setEditorReady(true);
      return;
    }

    // For video: create hidden video element if not present and play it silently in the background
    let hidden = hiddenVideoRef.current;
    if (!hidden) {
      hidden = document.createElement('video');
      hiddenVideoRef.current = hidden;
      hidden.muted = true;
      hidden.playsInline = true;
      hidden.crossOrigin = 'anonymous';
      hidden.src = previewURL;
      hidden.load();
    }

    await hidden.play().catch(()=>{});

    // set canvas size to video frame size once metadata available
    await new Promise((res) => {
      if (hidden.videoWidth > 0) {
        canvas.width = hidden.videoWidth;
        canvas.height = hidden.videoHeight;
        res();
      } else {
        const onMeta = () => {
          canvas.width = hidden.videoWidth || 1280;
          canvas.height = hidden.videoHeight || 720;
          hidden.removeEventListener('loadedmetadata', onMeta);
          res();
        };
        hidden.addEventListener('loadedmetadata', onMeta);
      }
    });

    setEditorReady(true);

    // frame loop function
    processing.current = true;
    const loop = async () => {
      if (!processing.current) return;
      try {
        // create ImageBitmap from current video frame (fast)
        const bitmap = await createImageBitmap(hidden);
        // send to worker
        try {
          workerRef.current.postMessage({ type: 'frame', bitmap }, [bitmap]);
        } catch (err) {
          // fallback: draw locally
          const ctx = canvas.getContext('2d');
          ctx.drawImage(hidden, 0, 0, canvas.width, canvas.height);
          try { bitmap.close && bitmap.close(); } catch {}
        }
      } catch (err) {
        // ignored
      }
      frameLoopRef.current = requestAnimationFrame(loop);
    };

    frameLoopRef.current = requestAnimationFrame(loop);
  };

  const stopFrameLoop = () => {
    processing.current = false;
    try { cancelAnimationFrame(frameLoopRef.current); } catch {}
    frameLoopRef.current = null;
  };

  /* ---------------- start editor flow ---------------- */
  useEffect(() => {
    // when entering editor state, prepare worker and start streaming frames
    if (!isEditing) return;

    const wCanvas = displayCanvasRef.current;
    if (!wCanvas) return;

    // create worker
    createWorker();
    // let worker know settings
    workerRef.current.postMessage({ type: 'updateSettings', settings: workerSettings });

    // start frame loop
    startFrameLoopToWorker();

    return () => {
      stopFrameLoop();
      // don't terminate worker here because user may reopen editor quickly; but if you want, call terminateWorker()
    };
    
  }, [isEditing, previewURL]);

  /* ---------------- update worker settings (filters/adjustments) ---------------- */
  useEffect(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type:'updateSettings', settings: workerSettings });
    }
  }, [workerSettings]);

  /* ---------------- export edited video/image by recording display canvas ---------------- */
  const exportEdited = async () => {
    const canvas = displayCanvasRef.current;
    if (!canvas) {
      alert('No editor canvas');
      return null;
    }

    if (mediaBlob && mediaBlob.type.startsWith('image')) {
      // simple export: canvas.toBlob
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
      return blob;
    }

    // For video: record canvas stream while hidden video plays (the worker should be processing frames into the canvas)
    const fps = 25;
    const stream = canvas.captureStream ? canvas.captureStream(fps) : null;
    if (!stream) {
      alert('Your browser does not support canvas.captureStream required for video export.');
      return null;
    }
    const recOptions = { mimeType: 'video/webm;codecs=vp8' };
    const recorder = new MediaRecorder(stream, recOptions);
    const outChunks = [];
    recorder.ondataavailable = e => { if (e.data && e.data.size) outChunks.push(e.data); };

    const startRec = new Promise((resolve, reject) => {
      recorder.onstop = () => {
        const outBlob = new Blob(outChunks, { type: outChunks[0]?.type || 'video/webm' });
        resolve(outBlob);
      };
      recorder.onerror = (err) => reject(err);
    });

    recorder.start(250);

    // play hidden video if not playing
    if (hiddenVideoRef.current) {
      try {
        hiddenVideoRef.current.currentTime = 0;
        await hiddenVideoRef.current.play();
      } catch (e) { /* ignore */ }
    }

    // when hidden video ends, stop recorder. we also set a safety timeout based on video duration
    const endHandler = () => {
      try { if (recorder && recorder.state !== 'inactive') recorder.stop(); } catch {}
    };
    if (hiddenVideoRef.current) {
      hiddenVideoRef.current.onended = endHandler;
      // safety timeout:
      const durationMs = (hiddenVideoRef.current.duration || 60) * 1000 + 5000;
      setTimeout(() => { try { if (recorder && recorder.state !== 'inactive') recorder.stop(); } catch {} }, durationMs);
    } else {
      // no hidden video (shouldn't happen), stop recording quickly
      setTimeout(() => { try { recorder.stop(); } catch {} }, 2000);
    }

    // wait for recorder to finish and return blob
    try {
      const exported = await startRec;
      return exported;
    } catch (err) {
      console.error('Export failed', err);
      return null;
    }
  };

  /* ---------------- postCaptured (unchanged behavior � uses mediaBlob) ---------------- */
  const postCaptured = async () => {
    if (!user) return alert('Login required');
    if (!mediaBlob) return alert('No media captured');
    try {
      const publicKey = genKey();
      const privateKey = genKey();
      const formData = new FormData();

      formData.append('caption', caption);
      formData.append('licenseType', license);
      formData.append('publicKey', publicKey);
      formData.append('privateKey', privateKey);
      formData.append('author', user.id);

      let mediaType = 'Text';
      if (mode === 'video') mediaType = 'Video';
      if (mode === 'image') mediaType = 'Image';
      if (mode === 'audio') mediaType = 'Music';
      if (mode === 'live') mediaType = 'Live';

      formData.append('mediaType', mediaType);

      if (license === 'Private') {
        const enc = await encryptText(privateKey, caption);
        formData.append('encryptedContent', enc);
        formData.append('content', '[private]');
      } else {
        formData.append('content', caption);
      }

      const ext = mode === 'image' ? '.jpg' : '.webm';
      formData.append('media', mediaBlob, `${user.id}_${Date.now()}${ext}`);

      await pb.collection('posts').create(formData);

      if (previewURL) URL.revokeObjectURL(previewURL);
      setMediaBlob(null);
      setPreviewURL(null);
      setIsEditing(false);
      alert('Posted successfully!');
      onClose && onClose();
    } catch (err) {
      console.error(err);
      alert('Post failed');
    }
  };

  /* ---------------- when user clicks "Apply & Save" in editor: export edited and replace mediaBlob ---------------- */
  const handleApplyEdits = async () => {
    setEditorReady(false);
    // export edited (image or video)
    const editedBlob = await exportEdited();
    if (!editedBlob) {
      alert('Failed to export edits.');
      setEditorReady(true);
      return;
    }
    setMediaBlob(editedBlob);
    if (previewURL) URL.revokeObjectURL(previewURL);
    setPreviewURL(URL.createObjectURL(editedBlob));
    setIsEditing(false);
    setEditorReady(false);
    // if you want, you can auto-post or return to preview UI
  };

  /* ---------------- cleanup when modal closed ---------------- */
  useEffect(() => {
    if (!open) {
      stopStream();
      stopFrameLoop();
      terminateWorker();
      try { hiddenVideoRef.current && hiddenVideoRef.current.pause(); } catch {}
      if (previewURL) URL.revokeObjectURL(previewURL);
      setPreviewURL(null);
      setMediaBlob(null);
      setIsEditing(false);
      setEditorReady(false);
      chunkBufferRef.current = [];
    }
    
  }, [open]);

  /* ---------------- UI render ---------------- */
  if (!open) return null;

  return (
    <div className="cam-modal-overlay" style={overlayStyle}>
      <style>{modalStyles}</style>
      <div className="cam-modal" style={modalStyle} role="dialog" aria-modal="true">
        <div className="cam-header" style={headerStyle}>
          <h3>Create � Capture</h3>
          <div>
            <button onClick={() => { stopStream(); onClose && onClose(); }} style={iconBtnStyle}>?</button>
          </div>
        </div>

        <div className="cam-body" style={bodyStyle}>
          <div className="cam-sidebar" style={sidebarStyle}>
            <button className={`mode-btn ${mode==="image"?"active":""}`} onClick={() => { setMode("image"); startStream(); }}>?? Image</button>
            <button className={`mode-btn ${mode==="video"?"active":""}`} onClick={() => { setMode("video"); startStream(); }}>?? Video</button>
            <button className={`mode-btn ${mode==="audio"?"active":""}`} onClick={() => { setMode("audio"); startStream(); }}>?? Audio</button>
            <button className={`mode-btn ${mode==="live"?"active":""}`} onClick={() => { setMode("live"); startStream(); }}>?? Live</button>
            <hr/>
            <small style={{color:'#bbb'}}>Recording limit (video): 5 minutes</small>
          </div>

          <div className="cam-main" style={mainStyle}>
            {/* PREVIEW / EDITOR */}
            {!isEditing && (
              <>
                <div className="preview" style={previewStyle}>
                  {(mode !== 'audio' && !previewURL) && (
                    <video ref={videoRef} style={{ width:'100%', height:'100%', background:'#000', transform:'scaleX(-1)' }} autoPlay muted playsInline />
                  )}

                  {(mode === 'audio' && !previewURL) && (
                    <audio ref={audioRef} controls style={{ width:'80%' }} />
                  )}

                  {previewURL && (
                    <div style={{ width:'100%', height:'100%' }}>
                      {(mode === 'image') && <img src={previewURL} alt="" style={{ maxWidth:'100%', maxHeight:'100%' }} />}
                      {(mode === 'video' || mode === 'live') && <video src={previewURL} controls style={{ width:'100%', height:'100%' }} />}
                      {(mode === 'audio') && <audio src={previewURL} controls style={{ width:'100%' }} />}
                    </div>
                  )}
                </div>

                <div className="controls" style={controlsStyle}>
                  {!previewURL && mode === 'image' && <button onClick={captureImage} style={recordBtnStyle}>?? Capture</button>}
                  {!previewURL && (mode === 'video' || mode === 'audio' || mode === 'live') && (
                    <>
                      {!recording ? <button onClick={startRecording} style={recordBtnStyle}>? Start</button> : <button onClick={stopRecording} style={stopBtnStyle}>? Stop</button>}
                      <div style={{ color:'#aaa', marginTop:6 }}>Timer: {Math.floor(timer/60)}:{String(timer%60).padStart(2,'0')}</div>
                    </>
                  )}
                  {previewURL && (
                    <div style={{ display:'flex', gap:10 }}>
                      {(mode === 'image' || mode === 'video') && <button onClick={() => { setIsEditing(true); setEditorReady(false); }} style={recordBtnStyle}>? Edit</button>}
                      <button onClick={postCaptured} style={postBtnStyle}>Post</button>
                      <button onClick={reRecord} style={reRecordBtnStyle}>Re-record</button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* EDITOR */}
            {isEditing && (
              <div style={{ display:'flex', gap:12 }}>
                <div style={{ flex:1, background:'#000', borderRadius:8, overflow:'hidden' }}>
                  <canvas ref={displayCanvasRef} style={{ width:'100%', display:'block' }} />
                </div>

                {/* Tools */}
                <div style={{ width:300, display:'flex', flexDirection:'column', gap:10 }}>
                  <div>
                    <strong>Filters</strong>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                      {["none","vintage","emboss","ink","edge","gaussianBlur","triangleBlur","hexPixelate","dotScreen","radialBlur"].map(f =>
                        <button key={f} onClick={() => setWorkerSettings(s => ({...s, filter: f}))} style={{ padding:8, background: workerSettings.filter===f ? '#ff6ec4':'#222', color:'#fff', border:'none', borderRadius:6 }}>{f}</button>
                      )}
                    </div>
                  </div>

                  <div>
                    <strong>Adjust</strong>
                    <label>Brightness</label>
                    <input type="range" min="-100" max="100" value={workerSettings.adjustments.brightness} onChange={e => setWorkerSettings(s => ({...s, adjustments:{...s.adjustments, brightness: Number(e.target.value)}}))} />
                    <label>Contrast</label>
                    <input type="range" min="-100" max="100" value={workerSettings.adjustments.contrast} onChange={e => setWorkerSettings(s => ({...s, adjustments:{...s.adjustments, contrast: Number(e.target.value)}}))} />
                    <label>Saturation</label>
                    <input type="range" min="-100" max="100" value={workerSettings.adjustments.saturation} onChange={e => setWorkerSettings(s => ({...s, adjustments:{...s.adjustments, saturation: Number(e.target.value)}}))} />
                    <label>Hue</label>
                    <input type="range" min="-180" max="180" value={workerSettings.adjustments.hue} onChange={e => setWorkerSettings(s => ({...s, adjustments:{...s.adjustments, hue: Number(e.target.value)}}))} />
                    <label>Sharpen</label>
                    <input type="range" min="0" max="100" value={workerSettings.adjustments.sharpen} onChange={e => setWorkerSettings(s => ({...s, adjustments:{...s.adjustments, sharpen: Number(e.target.value)}}))} />
                    <label>Blur</label>
                    <input type="range" min="0" max="10" value={workerSettings.adjustments.blur} onChange={e => setWorkerSettings(s => ({...s, adjustments:{...s.adjustments, blur: Number(e.target.value)}}))} />
                  </div>

                  <div>
                    <strong>Transform</strong>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => setWorkerSettings(s => ({...s, rotate: (s.rotate+90)%360}))} style={iconBtnStyle}>? Rotate</button>
                      <button onClick={() => setWorkerSettings(s => ({...s, flipH: !s.flipH}))} style={iconBtnStyle}>? Flip H</button>
                      <button onClick={() => alert('Crop tool coming soon')} style={iconBtnStyle}>Crop</button>
                    </div>
                  </div>

                  <div style={{ marginTop:'auto' }}>
                    <button onClick={() => workerRef.current && workerRef.current.postMessage({ type:'updateSettings', settings: workerSettings })} style={{ padding:10, width:'100%', marginBottom:8 }}>Apply Settings</button>
                    <button onClick={handleApplyEdits} style={{ padding:10, width:'100%', background:'#00c48c', border:'none' }}>Apply & Save</button>
                    <button onClick={() => { setIsEditing(false); stopFrameLoop(); }} style={{ padding:10, width:'100%', marginTop:6 }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        <div className="cam-footer" style={footerStyle}>
          {!isEditing && (
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Write caption..." style={{ padding:8, borderRadius:8, border:'1px solid #333', background:'#0b0b0d', color:'#fff' }} />
              <button onClick={postCaptured} style={postBtnStyle}>Post</button>
              <button onClick={() => { stopStream(); onClose && onClose(); }} style={footerCloseStyle}>Close</button>
            </div>
          )}

          {isEditing && (
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setIsEditing(false); stopFrameLoop(); }} style={footerCloseStyle}>Back</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- styles (same as your earlier styles) ---------- */
const overlayStyle = { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99999 };
const modalStyle = { width:"96%", maxWidth:1200, background:"#0b0b0d", borderRadius:14, border:"1px solid rgba(255,255,255,0.03)", boxShadow:"0 10px 40px rgba(0,0,0,0.6)", overflow:"hidden" };
const headerStyle = { padding:12, display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(255,255,255,0.02)" };
const bodyStyle = { display:"flex", minHeight:480 };
const sidebarStyle = { width:160, padding:16, borderRight:"1px solid rgba(255,255,255,0.02)", display:"flex", flexDirection:"column", gap:10, background:"linear-gradient(180deg, rgba(255,255,255,0.01), transparent)" };
const mainStyle = { flex:1, padding:14, display:"flex", flexDirection:"column", gap:12 };
const previewStyle = { flex:1, background:"#07070a", borderRadius:10, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" };
const controlsStyle = { paddingTop:8 };
const recordBtnStyle = { background:"linear-gradient(135deg,#ff6ec4,#7873f5)", color:"#fff", border:"none", padding:"10px 16px", borderRadius:10, cursor:"pointer" };
const stopBtnStyle = { background:"#111", color:"#fff", border:"1px solid #ff6ec4", padding:"10px 14px", borderRadius:10, cursor:"pointer" };
const postBtnStyle = { background:"#00c48c", color:"#071012", border:"none", padding:"10px 14px", borderRadius:10, cursor:"pointer" };
const reRecordBtnStyle = { background:"#222", color:"#fff", border:"1px solid #444", padding:"8px 12px", borderRadius:8, cursor:"pointer" };
const iconBtnStyle = { background:"transparent", color:"#fff", border:"1px solid rgba(255,255,255,0.04)", borderRadius:8, padding:"6px 8px", cursor:"pointer" };
const footerStyle = { padding:12, borderTop:"1px solid rgba(255,255,255,0.02)", display:"flex", alignItems:"center", gap:10, justifyContent:"space-between" };
const footerCloseStyle = { background:"transparent", color:"#fff", border:"1px solid rgba(255,255,255,0.04)", padding:"8px 12px", borderRadius:8 };
const modalStyles = `
  .mode-btn { display:block; padding:10px; border-radius:8px; background:transparent; color:#ddd; border:1px solid rgba(255,255,255,0.02); cursor:pointer; text-align:left; }
  .mode-btn.active { background: linear-gradient(90deg, rgba(255,110,196,0.08), rgba(120,115,245,0.06)); color:#fff; border-color: rgba(120,115,245,0.12); }
  @media (max-width:900px) {
    .cam-body { flex-direction: column; }
    .cam-sidebar { width:100%; display:flex; gap:8px; overflow:auto; padding:10px; }
    .cam-main { padding:10px; }
    .preview { height: 320px; }
  }
`;